import { Component, computed, inject, input, LOCALE_ID, OnInit, signal } from '@angular/core';
import { PharmacyService } from '../../../../services/pharmacy/pharmacy.service';
import { PharmacyVTO, RegisterPharamcyDTO } from '../../../../services/pharmacy/pharmacy.model';
import { CommonModule } from '@angular/common';
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { PharamcyNickname } from '../../../admin-customer-shared-components/create-pharmacy-component/pharamcy-nickname/pharamcy-nickname';
import { TaxIdentificationNumber } from '../../../admin-customer-shared-components/create-pharmacy-component/tax-identification-number/tax-identification-number';
import { PharmacyAddress } from '../../../admin-customer-shared-components/create-pharmacy-component/pharmacy-address/pharmacy-address';
import { CommercialRegestrationImage } from '../../../admin-customer-shared-components/create-pharmacy-component/pharmacyFileMgt/commercial-regestration-image/commercial-regestration-image';
import { TaxImage } from '../../../admin-customer-shared-components/create-pharmacy-component/pharmacyFileMgt/tax-image/tax-image';
import { PharmacyContractImage } from '../../../admin-customer-shared-components/create-pharmacy-component/pharmacyFileMgt/pharmacy-contract-image/pharmacy-contract-image';
import { NationalIdCard } from '../../../admin-customer-shared-components/create-pharmacy-component/pharmacyFileMgt/national-id-card/national-id-card';
import { PharmacyLicenseImage } from '../../../admin-customer-shared-components/create-pharmacy-component/pharmacyFileMgt/pharmacy-license-image/pharmacy-license-image';
import { CommercialRegisterationNumber } from '../../../admin-customer-shared-components/create-pharmacy-component/commercial-registeration-number/commercial-registeration-number';
import { PharmacistNationalId } from '../../../admin-customer-shared-components/create-pharmacy-component/pharmacist-national-id/pharmacist-national-id';
import { Notification } from '../../../shared/notification/notification';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { Error } from '../../../public-user-component/auth/shared/error/error';

import { PharmacyLocation } from '../../../admin-customer-shared-components/create-pharmacy-component/pharmacy-location/pharmacy-location';
import { urlValidator } from './new-request-form.validator';

@Component({
  selector: 'app-new-request-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PharamcyNickname,
    TaxIdentificationNumber,
    PharmacyAddress,
    CommercialRegestrationImage,
    TaxImage,
    PharmacyContractImage,
    NationalIdCard,
    PharmacyLicenseImage,
    CommercialRegisterationNumber,
    PharmacistNationalId,
    Notification,
    SuccessfulNotification,
    Error,
    PharmacyLocation,
  ],
  templateUrl: './new-request-form.html',
  styleUrl: './new-request-form.scss',
})
export class NewRequestForm implements OnInit {
  private router = inject(Router);
  private pharmacyService = inject(PharmacyService);
  private locale = inject(LOCALE_ID);
  type = input<'create' | 'edit'>('create');
  pharmacyData = input<PharmacyVTO>();
  editedFormChanged = signal<boolean>(false);
  errorEditPharamcy = signal<string>('');

  submissionError = signal<string>('');
  loading = signal(false);
  isNotificationVisible = signal(false);

  requestForm = new FormGroup({
    nickname: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(255),
        Validators.pattern(/^[A-Za-z0-9\u0600-\u06FF](?:[A-Za-z0-9\u0600-\u06FF -]*[A-Za-z0-9\u0600-\u06FF])?$/),
      ],
    }),
    taxIdentificationNumber: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(9),
        Validators.maxLength(9),
        Validators.pattern(/^[0-9]+$/),
      ],
    }),
    pharmacistNationalId: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(14),
        Validators.maxLength(14),
        Validators.pattern(/^[0-9]+$/),
      ],
    }),
    commercialRegisterNumber: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(20),
        Validators.pattern(/^(?! )[A-Za-z0-9\u0600-\u06FF\/\\-]+(?<! )$/),
      ],
    }),

    pharmacyAddress: new FormControl('', {
      validators: [Validators.required, Validators.minLength(10), Validators.maxLength(1000)],
    }),

    pharmacyLocation: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(500), urlValidator],
    }),

    commercialRegistrationFid: new FormControl('', [Validators.required]),
    taxRegistrationCertificateFid: new FormControl('', [Validators.required]),
    pharmacyContractFid: new FormControl('', [Validators.required]),
    pharmacyLicenseFid: new FormControl('', [Validators.required]),
    pharmacistNationalIdFid: new FormControl('', [Validators.required]),
  });

  onFileUploaded(fid: string, controlName: string) {
    this.requestForm.patchValue({ [controlName]: fid });
  }

  ngOnInit(): void {
    if (this.type() === 'edit' && this.pharmacyData()) {
      const data = this.pharmacyData();
      this.requestForm.patchValue({
        nickname: data?.nickName,
        commercialRegisterNumber: data?.commercialRegistrationNumber,
        pharmacistNationalId: data?.pharmacyOwnerNationalId,
        taxIdentificationNumber: data?.taxIdentificationNumber,
        pharmacyAddress: data?.address,

        pharmacyLocation: (data as any)?.location || (data?.request as any)?.location || '',
        commercialRegistrationFid: data?.commercialRegistrationFid,
        pharmacistNationalIdFid: data?.pharmacistNationalIdFid,
        pharmacyContractFid: data?.pharmacyContractFid,
        pharmacyLicenseFid: data?.pharmacyLicenseFid,
        taxRegistrationCertificateFid: data?.taxRegistrationCertificateFid,
      });
    }
  }

  onSubmit() {
    this.submissionError.set('');
    if (this.requestForm.invalid) {
      this.requestForm.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    const formValue = this.requestForm.value;

    const payload: RegisterPharamcyDTO = {
      nickName: formValue.nickname!,
      commercialRegistrationNumber: formValue.commercialRegisterNumber!,
      taxIdentificationNumber: formValue.taxIdentificationNumber!,
      pharmacyOwnerNationalId: formValue.pharmacistNationalId!,
      address: formValue.pharmacyAddress!,
      location: formValue.pharmacyLocation!,

      commercialRegistrationFid: formValue.commercialRegistrationFid!,
      taxRegistrationCertificateFid: formValue.taxRegistrationCertificateFid!,
      pharmacyContractFid: formValue.pharmacyContractFid!,
      pharmacyLicenseFid: formValue.pharmacyLicenseFid!,
      pharmacistNationalIdFid: formValue.pharmacistNationalIdFid!,
    };

    if (this.type() === 'create') {
      this.pharmacyService
        .registerPharmacy(payload)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: () => {
            this.isNotificationVisible.set(true);
            setTimeout(() => {
              this.router.navigate(['user/pharmacies']);
            }, 1000);
          },
          error: (error) => {
            this.submissionError.set(
              this.isArabic()
                ? error.messageAr || 'حدث بعض الخطأ!'
                : error.messageEn || 'An error occurred while submitting the request.',
            );
          },
        });
    }

    if (this.type() === 'edit') {
      this.pharmacyService
        .submitModification(this.pharmacyData()?.id!, payload)
        .pipe(
          finalize(() => {
            this.loading.set(false);
          }),
        )
        .subscribe({
          next: () => {
            this.isNotificationVisible.set(true);
            setTimeout(() => {
              this.isNotificationVisible.set(false);
              // this.router.navigate(['']);
            }, 700);
          },
          error: (err) => {
            this.errorEditPharamcy.set(
              this.isArabic() ? err.messageAr || 'حدث بعض الخطأ!' : err.messageEn || 'some error occured!',
            );
          },
        });
    }
  }

  onCancel() {
    this.router.navigate(['user/pharmacies']);
  }

  isArabic = computed(() => this.locale.startsWith('ar'));
  isEnglish = computed(() => this.locale.startsWith('en'));
}
